import { inject, Injectable } from '@angular/core';

import {
  COMMONS_ENVIRONMENT,
  CommonsEnvironment,
} from '../interface/environment/commons-environment.interface';
import { MaintenanceEnvironment } from '../interface/environment/maintenance-environment.interface';
import { BehaviorSubject, of, switchMap, timer } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { MaintenanceConfigDto } from '../dto/maintenance/maintenance-config.dto';
import { catchError, tap } from 'rxjs/operators';
import { Router } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Injectable({
  providedIn: 'root',
})
export class MaintenanceService {
  private readonly env: CommonsEnvironment = inject(COMMONS_ENVIRONMENT);
  private readonly httpClient = inject(HttpClient);
  private readonly router = inject(Router);
  private readonly DEFAULT_MAINTENANCE_CONFIG: MaintenanceConfigDto = {
    isActive: false,
  };
  private readonly MAINTENANCE_BYPASS_KEY = 'bypassMaintenance';

  private readonly maintenanceConfig$$ =
    new BehaviorSubject<MaintenanceConfigDto>(this.DEFAULT_MAINTENANCE_CONFIG);

  public readonly maintenanceConfig$ = this.maintenanceConfig$$.asObservable();

  private config: MaintenanceEnvironment = {
    configUrl: '/assets/maintenance.json',
    enabled: false,
    route: '/maintenance',
    refreshInterval: 2 * 60 * 1000,
  };

  public get maintenanceRoute(): string {
    return this.config.route;
  }
  public get isActive(): boolean {
    return this.maintenanceConfig$$.value.isActive;
  }
  /**
   * True when the current browser session may access the application during
   * maintenance (`bypassMaintenance` key present in sessionStorage).
   */
  public get isBypassed(): boolean {
    return sessionStorage.getItem(this.MAINTENANCE_BYPASS_KEY) !== null;
  }

  constructor() {
    this.init();
  }

  private isConfigurationValid() {
    return (
      this.config.refreshInterval > 0 &&
      this.config.configUrl !== '' &&
      this.config.route !== ''
    );
  }

  private init() {
    if (!this.env.maintenance?.enabled) {
      return;
    }
    this.config = {
      ...this.config,
      ...this.env.maintenance,
    };
    if (!this.isConfigurationValid()) {
      return;
    }
    this.startPolling();
  }

  private startPolling() {
    timer(0, this.config.refreshInterval)
      .pipe(
        takeUntilDestroyed(),
        switchMap(() =>
          this.httpClient
            .get<MaintenanceConfigDto>(this.config.configUrl)
            .pipe(catchError(() => of(this.DEFAULT_MAINTENANCE_CONFIG))),
        ),
        tap((config) => this.handleConfiguration(config)),
      )
      .subscribe();
  }

  private handleConfiguration(config: MaintenanceConfigDto): void {
    this.maintenanceConfig$$.next(config);

    if (
      config.isActive &&
      !this.isBypassed &&
      this.router.url !== this.config.route
    ) {
      this.router.navigate([this.maintenanceRoute]).then();
    }
  }
}
